// src/stores/TeamStore.js - Zustand Store replacing TeamSocketService + TeamProvider
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useTeamStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE (mirrors TeamProvider exactly)
    // ================================
    
    // Team data state
    teamMembers: [],
    permissions: {},
    teamStats: {},
    teamActivity: [],
    pendingInvitations: [],
    
    // Granular loading states for different operations
    teamLoading: false,
    permissionsLoading: false,
    roleUpdateLoading: false,
    activityLoading: false,
    inviteLoading: false,
    invitationsLoading: false,

    // Connection state
    isConnected: false,
    
    // Cache state (from TeamSocketService)
    cache: {
      teamMembers: [],
      permissions: {},
      teamStats: {},
      teamActivity: [],
      pendingInvitations: [],
      lastFetch: null
    },
    
    // Internal flags
    _initialized: false,
    
    // Cache duration (15 minutes)
    CACHE_DURATION: 15 * 60 * 1000,
    
    // ================================
    // SOCKET OPERATIONS (from TeamSocketService)
    // ================================
    
    /**
     * Request team members data - NO RETURN VALUE
     * @param {object} options - Loading options
     */
    loadTeamMembers: (options = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ TeamStore: Socket service not initialized');
        return;
      }
      
      console.log('👥 TeamStore: Loading team members');
      
      set({
        teamLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_TEAM_MEMBERS, {
        include_activity: options.include_activity !== false,
        include_permissions: options.include_permissions !== false
      });
    },
    
    /**
     * Request user permissions - NO RETURN VALUE
     */
    loadPermissions: () => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ TeamStore: Socket service not initialized');
        return;
      }
      
      console.log('🔐 TeamStore: Loading permissions');
      
      set({
        permissionsLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.CHECK_PERMISSIONS, {});
    },
    
    /**
     * Request team activity log - NO RETURN VALUE
     * @param {object} options - Activity filters
     */
    loadTeamActivity: (options = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ TeamStore: Socket service not initialized');
        return;
      }
      
      console.log('📊 TeamStore: Loading team activity');
      
      set({
        activityLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_TEAM_ACTIVITY, {
        days_back: options.days_back || 30,
        activity_types: options.activity_types || null,
        member_filter: options.member_filter || null
      });
    },
    
    /**
     * Request to load pending invitations - NO RETURN VALUE
     * @param {object} options - Optional filtering options
     */
    loadPendingInvitations: (options = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ TeamStore: Socket service not initialized');
        return;
      }
      
      console.log('📧 TeamStore: Loading pending invitations');
      
      set({
        invitationsLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_PENDING_INVITATIONS, {
        include_expired: options.include_expired || false,
        ...options
      });
    },
    
    /**
     * Request to update team member role - NO RETURN VALUE
     * @param {string} memberId - Member ID to update
     * @param {string} newRole - New role (admin, manager, staff)
     */
    updateMemberRole: (memberId, newRole) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ TeamStore: Socket service not initialized');
        return;
      }

      // Validate required fields
      if (!memberId || !newRole) {
        console.error('❌ TeamStore: Member ID and new role are required');
        return;
      }
      
      console.log('🔄 TeamStore: Updating member role:', memberId, '→', newRole);
      
      set({
        roleUpdateLoading: true
      });
      
      // Optimistic update - update UI immediately
      set(state => ({
        teamMembers: state.teamMembers.map(member =>
          member.id === memberId 
            ? { ...member, role: newRole }
            : member
        )
      }));
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_TEAM_MEMBER_ROLE, {
        member_id: memberId,
        new_role: newRole
      });
    },
    
    /**
     * Request to invite team member - NO RETURN VALUE
     * @param {string} email - Invitee email
     * @param {string} role - Role to assign
     * @param {string} message - Optional invitation message
     */
    inviteTeamMember: (email, role, message = '') => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ TeamStore: Socket service not initialized');
        return;
      }

      // Validate required fields
      if (!email || !role) {
        console.error('❌ TeamStore: Email and role are required');
        return;
      }
      
      console.log('📧 TeamStore: Inviting team member:', email, 'as', role);
      
      set({
        inviteLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.INVITE_TEAM_MEMBER, {
        email: email.trim(),
        role,
        message: message.trim()
      });
    },
    
    /**
     * Request to cancel team invitation - NO RETURN VALUE
     * @param {string} invitationId - Invitation ID to cancel
     */
    cancelInvitation: (invitationId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ TeamStore: Socket service not initialized');
        return;
      }

      // Validate required fields
      if (!invitationId) {
        console.error('❌ TeamStore: Invitation ID is required');
        return;
      }

      console.log('🚫 TeamStore: Cancelling invitation:', invitationId);
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.CANCEL_TEAM_INVITATION, {
        invitation_id: invitationId
      });
    },
    
    /**
     * Request to resend team invitation - NO RETURN VALUE
     * @param {string} invitationId - Invitation ID to resend
     */
    resendInvitation: (invitationId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ TeamStore: Socket service not initialized');
        return;
      }

      // Validate required fields
      if (!invitationId) {
        console.error('❌ TeamStore: Invitation ID is required');
        return;
      }

      console.log('🔄 TeamStore: Resending invitation:', invitationId);
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.RESEND_TEAM_INVITATION, {
        invitation_id: invitationId
      });
    },
    
    /**
     * Request to remove team member - NO RETURN VALUE
     * @param {string} memberId - Member ID to remove
     * @param {string} reason - Reason for removal
     */
    removeTeamMember: (memberId, reason = '') => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ TeamStore: Socket service not initialized');
        return;
      }

      // Validate required fields
      if (!memberId) {
        console.error('❌ TeamStore: Member ID is required');
        return;
      }

      console.log('🗑️ TeamStore: Removing team member:', memberId);
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.REMOVE_TEAM_MEMBER, {
        member_id: memberId,
        reason: reason.trim()
      });
    },

    reactivateTeamMember: (memberId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ TeamStore: Socket service not initialized');
        return;
      }

      // Validate required fields
      if (!memberId) {
        console.error('❌ TeamStore: Member ID is required');
        return;
      }

      console.log('🔄 TeamStore: Reactivating team member:', memberId);

      socketManager.universalHandler.emit(SOCKET_EVENTS.REACTIVATE_TEAM_MEMBER, {
        member_id: memberId
      });
    },

    // ================================
    // UTILITY FUNCTIONS (from TeamProvider)
    // ================================
    
    getMemberById: (memberId) => {
      const { teamMembers } = get();
      return teamMembers.find(m => m.id === memberId) || null;
    },
    
    getMembersByRole: (role) => {
      const { teamMembers } = get();
      return teamMembers.filter(m => m.role === role);
    },
    
    getOnlineMembers: () => {
      const { teamMembers } = get();
      return teamMembers.filter(m => m.presence?.status === 'online');
    },
    
    hasPermission: (permission) => {
      const { permissions } = get();
      const perms = permissions.permissions || {};
      return perms[permission] || false;
    },

    refreshTeamData: (options = {}) => {
      // Clear cache first
      set(state => ({
        cache: {
          ...state.cache,
          teamMembers: [],
          teamStats: {},
          lastFetch: null
        }
      }));
      
      // Then reload with force option
      get().loadTeamMembers({ ...options, force: true });
    },
    
    refreshPermissions: () => {
      // Clear permissions cache
      set(state => ({
        permissions: {},
        cache: {
          ...state.cache,
          permissions: {}
        }
      }));
      
      get().loadPermissions();
    },
    
    // Invitation utility functions
    getInvitationById: (id) => {
      const { pendingInvitations } = get();
      return pendingInvitations.find(inv => inv.id === id) || null;
    },
    
    getInvitationsByEmail: (email) => {
      const { pendingInvitations } = get();
      return pendingInvitations.filter(inv => 
        inv.email.toLowerCase() === email.toLowerCase()
      );
    },
    
    hasInvitationForEmail: (email) => {
      const { pendingInvitations } = get();
      return pendingInvitations.some(inv => 
        inv.email.toLowerCase() === email.toLowerCase()
      );
    },
    
    // Cache utilities
    getCachedData: () => {
      const { cache } = get();
      return {
        teamMembers: [...cache.teamMembers],
        permissions: { ...cache.permissions },
        teamStats: { ...cache.teamStats },
        teamActivity: [...cache.teamActivity],
        pendingInvitations: [...cache.pendingInvitations],
        lastFetch: cache.lastFetch
      };
    },
    
    isDataFresh: () => {
      const { cache, CACHE_DURATION } = get();
      return cache.lastFetch && 
             (Date.now() - cache.lastFetch) < CACHE_DURATION;
    },
    
    // ================================
    // COMPUTED VALUES (from TeamProvider - computed in hook)
    // ================================
    
    getPermissionHelpers: () => {
      const { permissions } = get();
      const perms = permissions.permissions || {};
      const roleInfo = permissions.role_info || {};
      
      return {
        // Role checks
        isAdmin: roleInfo.is_admin || false,
        isManager: roleInfo.is_manager || false,
        isStaff: roleInfo.is_staff || false,
        currentRole: roleInfo.role || '',
        
        // Team management permissions
        canManageTeam: perms.manage_team || false,
        canAddTeamMembers: perms.add_team_members || false,
        canRemoveTeamMembers: perms.remove_team_members || false,
        canUpdateTeamRoles: perms.update_team_roles || false,
        canViewTeamActivity: perms.view_team_activity || false,
        
        // Invitation-specific permissions
        canInviteMembers: perms.add_team_members || false,
        canManageInvitations: perms.manage_team || false,
        canCancelInvitations: perms.manage_team || false,
        canResendInvitations: perms.manage_team || false,
        
        // Business management permissions
        canManageBusinessSettings: perms.manage_business_settings || false,
        canManageBusinessHours: perms.manage_business_hours || false,
        canManageServices: perms.manage_services || false,
        canViewBusinessAnalytics: perms.view_business_analytics || false,
        
        // Customer and event permissions
        canManageCustomers: perms.manage_customers || false,
        canManageEvents: perms.manage_events || false,
        canViewAllEvents: perms.view_all_events || false,
        
        // Financial permissions
        canManageInvoices: perms.manage_invoices || false,
        canViewFinancials: perms.view_financials || false
      };
    },
    
    getTeamStatsHelpers: () => {
      const { teamMembers, teamActivity, pendingInvitations } = get();
      
      return {
        totalMembers: teamMembers.length,
        onlineMembers: teamMembers.filter(m => m.presence?.status === 'online').length,
        adminMembers: teamMembers.filter(m => m.role === 'admin').length,
        managerMembers: teamMembers.filter(m => m.role === 'manager').length,
        staffMembers: teamMembers.filter(m => m.role === 'staff').length,
        recentActivityCount: teamActivity.filter(a => {
          const activityDate = new Date(a.created_at);
          const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
          return activityDate > sevenDaysAgo;
        }).length,
        
        // Invitation stats
        pendingInvitationsCount: pendingInvitations.length,
        invitationsByRole: pendingInvitations.reduce((acc, inv) => {
          acc[inv.role] = (acc[inv.role] || 0) + 1;
          return acc;
        }, {}),
        oldestInvitation: pendingInvitations.length > 0 
          ? pendingInvitations.reduce((oldest, inv) => 
              new Date(inv.invited_at) < new Date(oldest.invited_at) ? inv : oldest
            )
          : null,
        expiredInvitations: pendingInvitations.filter(inv => inv.is_expired).length
      };
    },
    
    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================
    
    _setTeamMembersLoaded: (data) => {
      console.log('📡 TeamStore: Team members loaded:', data.team_members?.length || 0);
      
      set(state => ({
        teamMembers: data.team_members || [],
        teamStats: data.team_stats || {},
        teamLoading: false,
        cache: {
          ...state.cache,
          teamMembers: data.team_members || [],
          teamStats: data.team_stats || {},
          lastFetch: Date.now()
        }
      }));
    },
    
    _setPermissionsLoaded: (data) => {
      console.log('📡 TeamStore: Permissions loaded');
      
      set(state => ({
        permissions: data,
        permissionsLoading: false,
        cache: {
          ...state.cache,
          permissions: data,
          lastFetch: Date.now()
        }
      }));
    },
    
    _setTeamActivityLoaded: (data) => {
      console.log('📡 TeamStore: Team activity loaded:', data.activities?.length || 0);
      
      set(state => ({
        teamActivity: data.activities || [],
        activityLoading: false,
        cache: {
          ...state.cache,
          teamActivity: data.activities || []
        }
      }));
    },
    
    _setPendingInvitationsLoaded: (data) => {
      console.log('📡 TeamStore: Pending invitations loaded:', data.invitations?.length || 0);
      
      set(state => ({
        pendingInvitations: data.invitations || [],
        invitationsLoading: false,
        cache: {
          ...state.cache,
          pendingInvitations: data.invitations || []
        }
      }));
    },
    
    _updateMemberRole: (data) => {
      console.log('📡 TeamStore: Member role updated:', data.member?.name, '→', data.new_role);
      
      set(state => {
        const updatedMember = data.member;
        const updatedTeamMembers = state.teamMembers.map(member =>
          member.id === updatedMember.id ? { ...member, ...updatedMember } : member
        );
        
        return {
          teamMembers: updatedTeamMembers,
          roleUpdateLoading: false,
          cache: {
            ...state.cache,
            teamMembers: updatedTeamMembers
          }
        };
      });
    },
    
    _handleRoleChanged: (data) => {
      console.log('📡 TeamStore: Your role changed to:', data.new_role);
      
      // Clear permissions cache and reload
      set(state => ({
        permissions: {},
        permissionsLoading: true,
        cache: {
          ...state.cache,
          permissions: {}
        }
      }));
      
      get().loadPermissions();
    },
    
    _addInvitation: (data) => {
      console.log('📡 TeamStore: Member invited:', data.invitation?.email);
      
      set(state => {
        const updatedInvitations = data.invitation 
          ? [data.invitation, ...state.pendingInvitations]
          : state.pendingInvitations;
        
        return {
          pendingInvitations: updatedInvitations,
          inviteLoading: false,
          cache: {
            ...state.cache,
            pendingInvitations: updatedInvitations
          }
        };
      });
    },
    
    _removeInvitation: (data) => {
      console.log('📡 TeamStore: Invitation cancelled:', data.invitation?.email);
      
      set(state => {
        const updatedInvitations = state.pendingInvitations.filter(
          invitation => invitation.id !== data.invitation?.id
        );
        
        return {
          pendingInvitations: updatedInvitations,
          cache: {
            ...state.cache,
            pendingInvitations: updatedInvitations
          }
        };
      });
    },

    _updateInvitation: (data) => {
      console.log('📡 TeamStore: Invitation resent:', data.invitation?.email);
      
      set(state => {
        const updatedInvitation = data.invitation;
        const updatedInvitations = state.pendingInvitations.map(invitation => 
          invitation.id === updatedInvitation.id 
            ? { ...invitation, ...updatedInvitation }
            : invitation
        );
        
        return {
          pendingInvitations: updatedInvitations,
          cache: {
            ...state.cache,
            pendingInvitations: updatedInvitations
          }
        };
      });
    },

    _removeMember: (data) => {
      console.log('📡 TeamStore: Member deactivated:', data.member?.member_name || data.member_id);

      set(state => {
        // Backend sends { member: { member_id, is_active: false, ... }, removed_by: {...} }
        const deactivatedMemberId = data.member?.member_id || data.member_id;
        const updatedTeamMembers = state.teamMembers.map(member =>
          member.id === deactivatedMemberId
            ? { ...member, is_active: false }
            : member
        );

        return {
          teamMembers: updatedTeamMembers,
          cache: {
            ...state.cache,
            teamMembers: updatedTeamMembers
          }
        };
      });
    },

    _reactivateMember: (data) => {
      console.log('📡 TeamStore: Member reactivated:', data.member?.member_name || data.member_id);

      set(state => {
        // Backend sends { member: { member_id, is_active: true, ... }, reactivated_by: {...} }
        const reactivatedMemberId = data.member?.member_id || data.member_id;
        const updatedTeamMembers = state.teamMembers.map(member =>
          member.id === reactivatedMemberId
            ? { ...member, is_active: true }
            : member
        );

        return {
          teamMembers: updatedTeamMembers,
          cache: {
            ...state.cache,
            teamMembers: updatedTeamMembers
          }
        };
      });
    },

    // ================================
    // INTERNAL METHODS (socket manager, initialization)
    // ================================
    
    _getSocketManager: () => {
      // Access socket manager from window global
      return { 
        socketManager: typeof window !== 'undefined' ? window.__socketManager : null 
      };
    },
    
    _initialize: (socketManager) => {
      console.log('🔌 TeamStore: Initializing with socket manager');

      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }

      set({
        isConnected: true,
        _initialized: true
      });

      // Auto-load all team data on initialization (matches pattern of other stores)
      // Backend will reject non-admins with permission errors for team/invitations
      console.log('🔄 TeamStore: Auto-loading team data');
      get().loadPermissions();
      get().loadTeamMembers();
      get().loadPendingInvitations();
    },
    
    _setConnectionState: (connected) => {
      set({ isConnected: connected });
    },
    
    _handleAuthReset: () => {
      console.log('🧹 TeamStore: Resetting all state due to auth reset');
      
      set({
        // Reset all state to initial values
        teamMembers: [],
        permissions: {},
        teamStats: {},
        teamActivity: [],
        pendingInvitations: [],
        teamLoading: false,
        permissionsLoading: false,
        roleUpdateLoading: false,
        activityLoading: false,
        inviteLoading: false,
        invitationsLoading: false,
        isConnected: false,
        cache: {
          teamMembers: [],
          permissions: {},
          teamStats: {},
          teamActivity: [],
          pendingInvitations: [],
          lastFetch: null
        },
        _initialized: false
      });
    }
  }))
);

// ================================
// SOCKET EVENT LISTENERS SETUP (Global initialization)
// ================================


export const initializeTeamStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }
  
  console.log('👂 TeamStore: Setting up socket event listeners');
  
  const { universalHandler } = socketManager;
  
  // Set up universal response listener for all team events
  const handleSocketResponse = (responseData) => {
    const eventName = responseData.event;
    const store = useTeamStore.getState();
    
    switch (eventName) {
      // ✅ SUCCESS EVENTS
      case SOCKET_EVENTS.TEAM_MEMBERS_LOADED:
        store._setTeamMembersLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.PERMISSIONS_LOADED:
        store._setPermissionsLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.TEAM_ACTIVITY_LOADED:
        store._setTeamActivityLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.PENDING_INVITATIONS_LOADED:
        store._setPendingInvitationsLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.TEAM_MEMBER_ROLE_UPDATED:
        store._updateMemberRole(responseData.data);
        break;
        
      case SOCKET_EVENTS.ROLE_CHANGED:
        store._handleRoleChanged(responseData.data);
        break;
        
      case SOCKET_EVENTS.TEAM_MEMBER_INVITED:
        store._addInvitation(responseData.data);
        break;
        
      case SOCKET_EVENTS.TEAM_INVITATION_CANCELLED:
        store._removeInvitation(responseData.data);
        break;
        
      case SOCKET_EVENTS.TEAM_INVITATION_RESENT:
        store._updateInvitation(responseData.data);
        break;
        
      case SOCKET_EVENTS.TEAM_MEMBER_REMOVED:
        store._removeMember(responseData.data);
        break;

      case SOCKET_EVENTS.TEAM_MEMBER_REACTIVATED:
        store._reactivateMember(responseData.data);
        break;

      // ❌ ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_TEAM_MEMBERS_FAILED:
        set({ teamLoading: false });
        break;

      case SOCKET_EVENTS.CHECK_PERMISSIONS_FAILED:
        set({ permissionsLoading: false });
        break;

      case SOCKET_EVENTS.GET_TEAM_ACTIVITY_FAILED:
        set({ activityLoading: false });
        break;

      case SOCKET_EVENTS.GET_PENDING_INVITATIONS_FAILED:
        set({ invitationsLoading: false });
        break;

      case SOCKET_EVENTS.UPDATE_TEAM_MEMBER_ROLE_FAILED:
        set({ roleUpdateLoading: false });
        break;

      case SOCKET_EVENTS.INVITE_TEAM_MEMBER_FAILED:
        set({ inviteLoading: false });
        break;

      case SOCKET_EVENTS.CANCEL_TEAM_INVITATION_FAILED:
      case SOCKET_EVENTS.RESEND_TEAM_INVITATION_FAILED:
      case SOCKET_EVENTS.REMOVE_TEAM_MEMBER_FAILED:
      case SOCKET_EVENTS.REACTIVATE_TEAM_MEMBER_FAILED:
        // Just log - NotificationListener shows error to user
        console.log('❌ TeamStore: Operation failed -', responseData.message);
        break;

      default:
        // Not a team-related event, ignore
        break;
    }
  };
  
  // Set up individual event listeners using existing system
  const teamEvents = [
    // Success events
    SOCKET_EVENTS.TEAM_MEMBERS_LOADED,
    SOCKET_EVENTS.PERMISSIONS_LOADED,
    SOCKET_EVENTS.TEAM_ACTIVITY_LOADED,
    SOCKET_EVENTS.PENDING_INVITATIONS_LOADED,
    SOCKET_EVENTS.TEAM_MEMBER_ROLE_UPDATED,
    SOCKET_EVENTS.ROLE_CHANGED,
    SOCKET_EVENTS.TEAM_MEMBER_INVITED,
    SOCKET_EVENTS.TEAM_INVITATION_CANCELLED,
    SOCKET_EVENTS.TEAM_INVITATION_RESENT,
    SOCKET_EVENTS.TEAM_MEMBER_REMOVED,
    SOCKET_EVENTS.TEAM_MEMBER_REACTIVATED,
    // Error events
    SOCKET_EVENTS.GET_TEAM_MEMBERS_FAILED,
    SOCKET_EVENTS.CHECK_PERMISSIONS_FAILED,
    SOCKET_EVENTS.GET_TEAM_ACTIVITY_FAILED,
    SOCKET_EVENTS.GET_PENDING_INVITATIONS_FAILED,
    SOCKET_EVENTS.UPDATE_TEAM_MEMBER_ROLE_FAILED,
    SOCKET_EVENTS.INVITE_TEAM_MEMBER_FAILED,
    SOCKET_EVENTS.CANCEL_TEAM_INVITATION_FAILED,
    SOCKET_EVENTS.RESEND_TEAM_INVITATION_FAILED,
    SOCKET_EVENTS.REMOVE_TEAM_MEMBER_FAILED,
    SOCKET_EVENTS.REACTIVATE_TEAM_MEMBER_FAILED
  ];
  
  // Set up individual event listeners using existing system
  teamEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      handleSocketResponse({ event: eventName, data });
    });
  });
  
  // Set up auth reset listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useTeamStore.getState()._handleAuthReset();
    });
  }
  
  // Initialize the store
  useTeamStore.getState()._initialize(socketManager);
  
  console.log('✅ TeamStore: Socket event listeners initialized');
};

export default useTeamStore;